/**
 * "What are you testing?" — the screen between choosing a project and seeing a
 * register.
 *
 * It exists because the platform now holds more than one kind of equipment and
 * more than one Cx level, and the engineer's answer decides everything
 * downstream: which script renders, which shots are required, which folder the
 * photos land in, and which report format comes out at the end. Guessing it from
 * the register would be guessing.
 *
 * The screen shows levels that are not built, disabled, rather than hiding them.
 * An engineer who cannot find CxL2 needs to know it does not exist yet, not
 * wonder whether they are on the wrong screen. The same goes for an equipment
 * type with no content at the chosen level.
 */

import { useMemo, useState } from 'react';

import type { CxLevel } from '../domain/catalogue.js';
import { type EquipmentDef, EQUIPMENT, catalogueFor, levelsFor } from '../domain/equipment.js';

export interface TargetPickerProps {
  projectName: string;
  onChoose: (equipmentType: string, level: CxLevel) => void;
}

/** How many tests and checks a target would put in front of the engineer. */
export function targetSize(equipmentType: string, level: CxLevel): { tests: number; checks: number } | null {
  try {
    const tests = catalogueFor(equipmentType, level);
    return { tests: tests.length, checks: tests.reduce((n, t) => n + t.checks.length, 0) };
  } catch {
    return null;
  }
}

export function TargetPicker({ projectName, onChoose }: TargetPickerProps) {
  const [equipment, setEquipment] = useState<EquipmentDef | null>(null);

  const levels = useMemo(() => (equipment ? levelsFor(equipment.type) : []), [equipment]);

  if (!equipment) {
    return (
      <section className="picker" data-testid="target-picker">
        <h2>What are you testing?</h2>
        <p className="picker-sub">{projectName}</p>
        <ul className="picker-list">
          {EQUIPMENT.map((e) => {
            const open = levelsFor(e.type).some((l) => l.available);
            return (
              <li key={e.type}>
                <button
                  type="button"
                  className="picker-item"
                  data-testid={`equipment-${e.shortCode}`}
                  disabled={!open}
                  onClick={() => setEquipment(e)}
                >
                  <span className="picker-code">{e.shortCode}</span>
                  <span className="picker-body">
                    <strong>{e.name}</strong>
                    <small>{e.note}</small>
                  </span>
                  {open ? null : <span className="picker-flag">no tests yet</span>}
                </button>
              </li>
            );
          })}
        </ul>
      </section>
    );
  }

  return (
    <section className="picker" data-testid="level-picker">
      <button type="button" className="picker-back" onClick={() => setEquipment(null)} data-testid="change-equipment">
        ← {equipment.name}
      </button>
      <h2>Which level?</h2>
      <ul className="picker-list">
        {levels.map((l) => {
          const size = l.available ? targetSize(equipment.type, l.level) : null;
          return (
            <li key={l.level}>
              <button
                type="button"
                className="picker-item"
                data-testid={`level-${l.level}`}
                disabled={!l.available}
                onClick={() => onChoose(equipment.type, l.level)}
              >
                <span className="picker-code">{l.level.replace('Cx', '')}</span>
                <span className="picker-body">
                  <strong>{l.name}</strong>
                  <small>{l.note}</small>
                </span>
                {size ? (
                  <span className="picker-count">
                    {size.tests} tests · {size.checks} checks
                  </span>
                ) : (
                  <span className="picker-flag">not built</span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
